<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <AdminPageHeader
      title="Item Claims"
      subtitle="Manage lost & found item claims"
      icon="fas fa-hand-holding"
      gradient="blue"
    />

    <BRow class="py-2">
      <BCol
        cols="12"
        md="4"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableData.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Item"
          label-for="filterItem"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableSelected.lfItem"
            input-id="filterItem"
            label="label"
            :options="options.lfItems"
            clearable
            append-to-body
            :calculate-position="calculateDropPosition"
            :disabled="tableData.busy"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Student"
          label-for="filterStudent"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableSelected.student"
            input-id="filterStudent"
            label="label"
            :options="options.students"
            clearable
            append-to-body
            :calculate-position="calculateDropPosition"
            :disabled="tableData.busy"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="2"
      >
        <BFormGroup
          label="Action"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BButton
            size="sm"
            type="button"
            variant="primary"

            @click.prevent="createItem"
          >
            Add Claim
          </BButton>
        </BFormGroup>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableData"
      :provider="tableProvider"
      :fields="tableData.fields"
      :filter="tableData.filter"
      :filter-on="tableData.filterOn"
      :sort-by="tableData.sortBy"
      :sort-desc="tableData.sortDesc"
      :sort-direction="tableData.sortDirection"
      :total-rows="tableData.totalRows"
      :current-page="tableData.currentPage"
      :per-page="tableData.perPage"
      :stacked="tableData.stacked"
      :busy="tableData.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
          <BButton
            size="sm"
            variant="primary"
            :disabled="tableData.busy"
            @click.prevent="editItem(item)"
          >
            Edit
          </BButton>
          <BButton
            size="sm"
            variant="danger"
            :disabled="tableData.busy"
            @click.prevent="deleteItemConfirm(item)"
          >
            Delete
          </BButton>
        </div>
      </template>

      <template #cell(lf_item)="{ item }">
        {{ item.lf_item ? `${item.lf_item.reference_number} - ${item.lf_item.title}` : 'N/A' }}
      </template>

      <template #cell(claimed_by_student)="{ item }">
        {{ item.claimed_by_student ? `${item.claimed_by_student.last_name}, ${item.claimed_by_student.first_name}` : 'N/A' }}
      </template>

      <template #cell(claim_date)="{ item }">
        {{ item.claim_date ? dateShortFormatter(item.claim_date) : 'N/A' }}
      </template>

      <template #cell(verified_by)="{ item }">
        {{ formatVerifier(item) }}
      </template>
    </CustomTable>

    <BModal
      ref="modalForm"
      size="lg"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      :title="state.editing ? 'Edit Claim' : 'Add Claim'"
    >
      <ValidationObserver ref="formData">
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>
            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="lf_item_id"
                name="item"
                rules="required"
              >
                <BFormGroup
                  label="Item"
                  label-for="fItem"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <VSelect
                    v-model="selected.lfItem"
                    placeholder="select item"
                    input-id="fItem"
                    label="label"
                    append-to-body
                    :options="options.lfItems"
                    :disabled="state.busy"
                    :reduce="opt => opt.id"
                    :calculate-position="calculateDropPosition"
                    :class="{ 'is-invalid': errors.length > 0 }"
                    @input="val => formData.lf_item_id = val"
                  >
                    <template #option="{ label }">
                      <div
                        class="text-break text-wrap"
                        v-text="label"
                      />
                    </template>
                    <template #no-options="">
                      no available items
                    </template>
                  </VSelect>
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="claimed_by_student_id"
                name="student"
                rules="required"
              >
                <BFormGroup
                  label="Claimed By (Student)"
                  label-for="fStudent"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <VSelect
                    v-model="selected.student"
                    placeholder="select student"
                    input-id="fStudent"
                    label="label"
                    append-to-body
                    :options="options.students"
                    :disabled="state.busy"
                    :reduce="opt => opt.id"
                    :calculate-position="calculateDropPosition"
                    :class="{ 'is-invalid': errors.length > 0 }"
                    @input="val => formData.claimed_by_student_id = val"
                  >
                    <template #option="{ label }">
                      <div
                        class="text-break text-wrap"
                        v-text="label"
                      />
                    </template>
                    <template #no-options="">
                      no available students
                    </template>
                  </VSelect>
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
                vid="claim_date"
                name="claim date"
                rules="required"
              >
                <BFormGroup
                  label="Claim Date"
                  label-for="fClaimDate"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fClaimDate"
                    v-model="formData.claim_date"
                    type="date"
                    autocomplete="off"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
              md="6"
            >
              <BFormGroup
                label="Verified By"
                label-for="fVerifiedBy"
                label-class="font-weight-bolder"
                class="mb-2"
              >
                <BFormInput
                  id="fVerifiedBy"
                  :value="currentUser ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || currentUser.username || 'N/A' : 'N/A'"
                  disabled
                />
              </BFormGroup>
            </BCol>

            <BCol cols="12">
              <ValidationProvider
                v-slot="{ errors }"
                vid="remarks"
                name="remarks"
              >
                <BFormGroup
                  label="Remarks"
                  label-for="fRemarks"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormTextarea
                    id="fRemarks"
                    v-model="formData.remarks"
                    placeholder="enter remarks here"
                    rows="3"
                    :state="errors.length > 0 ? false : null"
                    :disabled="state.busy"
                  />
                  <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>
          </BRow>
        </form>
      </ValidationObserver>

      <template #modal-footer="{ cancel }">
        <BButton
          variant="primary"
          :disabled="state.busy"
          @click.prevent="validateForm"
        >
          {{ state.editing ? 'Update' : 'Create' }}
        </BButton>
        <BButton
          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>
    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'

import AdminPageHeader from '@/components/core/AdminPageHeader.vue'
import { SAdminLfItemClaim, SSharedList } from '@/services'

export default {
  name: 'VAdminLfItemClaims',

  components: { AdminPageHeader },

  mixins: [MxMisc, MxFormatter],

  middleware: ['auth', 'admin'],

  metaInfo: () => ({ title: 'Item Claims' }),

  data () {
    return {
      state: { busy: false, editing: false },
      selected: { lfItem: null, student: null },
      tableSelected: { lfItem: null, student: null },
      options: { lfItems: [], students: [] },
      formData: {
        id: '',
        lf_item_id: '',
        claimed_by_student_id: '',
        claim_date: '',
        verified_by: null,
        remarks: ''
      },
      tableData: {
        busy: false,
        filter: {
          search: '',
          lf_item: '',
          claimed_by_student: ''
        },
        filterOn: [],
        stacked: this.isMobile(),
        sortBy: 'id',
        sortDesc: true,
        sortDirection: 'desc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 12, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 1, key: 'lf_item', label: 'Item', class: 'min-w-200px' },
          { mobile: 2, key: 'claimed_by_student', label: 'Claimed By', class: 'min-w-150px' },
          { mobile: 3, key: 'claim_date', label: 'Claim Date', class: 'min-w-120px' },
          { mobile: 4, key: 'verified_by', label: 'Verified By', class: 'min-w-120px' },
          { mobile: 5, key: 'remarks', class: 'min-w-200px' },
          { mobile: 6, key: 'updated_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: mapGetters({ currentUser: 'auth/user' }),

  watch: {
    'tableSelected.lfItem' (item) {
      this.tableData.filter.lf_item = item?.id || ''
    },

    'tableSelected.student' (student) {
      this.tableData.filter.claimed_by_student = student?.id || ''
    }
  },

  mounted () {
    this.loadOptions()
  },

  methods: {
    async loadOptions () {
      const [itemsRes, studentsRes] = await Promise.all([
        SSharedList.getActiveLfItems({ per_page: 1000, sort: 'updated_at', sort_desc: true }).catch(() => ({ data: [] })),
        SSharedList.getActiveStudents({ per_page: 1000, sort: 'last_name', sort_desc: false }).catch(() => ({ data: [] }))
      ])
      this.options.lfItems = (itemsRes.data?.data || []).map(i => ({ id: i.id, label: `${i.reference_number} - ${i.title}` }))
      this.options.students = (studentsRes.data?.data || []).map(s => ({ id: s.id, label: `${s.last_name}, ${s.first_name} (${s.id_number})` }))
    },

    formatVerifier (item) {
      const verifier = item?.verified_by

      if (verifier && typeof verifier === 'object') {
        const first = verifier.first_name || ''
        const last = verifier.last_name || ''
        const full = `${first} ${last}`.trim()
        return full || verifier.username || `User #${verifier.id || ''}`.trim()
      }

      if (typeof verifier === 'string') {
        try {
          const parsed = JSON.parse(verifier)
          if (parsed && typeof parsed === 'object') {
            const first = parsed.first_name || ''
            const last = parsed.last_name || ''
            const full = `${first} ${last}`.trim()
            return full || parsed.username || `User #${parsed.id || ''}`.trim()
          }
        } catch {
          // Keep original value when it's not JSON.
        }
      }

      return verifier || 'N/A'
    },

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableData.busy = true
      return await SAdminLfItemClaim.getList({
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        lf_item: filter.lf_item,
        claimed_by_student: filter.claimed_by_student
      }).then(({ data }) => {
        this.tableData.totalRows = data.total_rows
        return data.items
      }).catch(() => { this.tableData.totalRows = 0; return [] }).finally(() => { this.tableData.busy = false })
    },

    createItem () {
      this.state.editing = false
      this.selected = { lfItem: null, student: null }
      this.formData = {
        id: '',
        lf_item_id: '',
        claimed_by_student_id: '',
        claim_date: '',
        verified_by: this.currentUser?.id || null,
        remarks: ''
      }
      this.$refs.modalForm.show()
    },

    editItem (item) {
      this.state.editing = true
      this.selected.lfItem = item.lf_item_id
      this.selected.student = item.claimed_by_student_id
      this.formData = {
        id: item.id,
        lf_item_id: item.lf_item_id,
        claimed_by_student_id: item.claimed_by_student_id,
        claim_date: item.claim_date,
        verified_by: this.currentUser?.id || item.verified_by?.id || item.verified_by || null,
        remarks: item.remarks || ''
      }
      this.$refs.modalForm.show()
    },

    async validateForm () {
      await this.$refs.formData.validate().then(async valid => {
        if (!valid) return this.swalInvalid()
        this.swalConfirm({
          html: this.state.editing ? '<h6>Update this Claim?</h6>' : '<h6>Create this Claim?</h6>',
          confirmButtonText: this.state.editing ? 'Update' : 'Create',
          preConfirm: async () => {
            this.state.busy = true
            const op = this.state.editing
              ? SAdminLfItemClaim.update(this.formData.id, this.formData)
              : SAdminLfItemClaim.store(this.formData)
            return op.then(({ data: { message } }) => {
              this.swalSuccess(message).then(() => { this.$refs.modalForm.hide(); this.$refs.tableData.refetch() })
            }).catch(({ message }) => {
              if (message?.errors) this.$refs.formData.setErrors(message.errors)
            }).finally(() => { this.state.busy = false })
          }
        })
      })
    },

    async deleteItemConfirm (item) {
      const result = await this.$swal({ title: 'Delete Claim', text: 'Are you sure you want to delete this claim?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Yes, delete it!' })
      if (result.isConfirmed) {
        this.state.busy = true
        try {
          await SAdminLfItemClaim.destroy(item.id)
          this.swalSuccess('Claim deleted successfully')
          this.$refs.tableData.refetch()
        } catch (error) {
          this.swalError(error.response?.data?.message || 'Failed to delete')
        } finally { this.state.busy = false }
      }
    }
  }
}
</script>
